import { AvatarFallback } from '@radix-ui/react-avatar';
import { formatDistance } from 'date-fns';
import { de } from 'date-fns/locale';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileId } from '@/features/org/hooks/use-profile-id';
import { CommentBase } from '@/shared/components/comments/comment-type';
import { RoundedDropdownMenu } from '@/shared/components/rounded-dropdown-menu';
import { Avatar, AvatarImage } from '@/shared/components/ui/avatar';
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
  CardFooter,
  CardDescription,
} from '@/shared/components/ui/card';
import { DropdownMenuItem } from '@/shared/components/ui/dropdown';
import { getInitials } from '@/shared/utils/avatar-util';

interface CommentItemProps {
  comment: CommentBase;
  onDelete: (id: number) => void;
  onUpdateSeen: (id: number) => void;
  disableSeen: boolean;
}

export const CommentItem = ({
  comment,
  onDelete,
  onUpdateSeen,
  disableSeen,
}: CommentItemProps) => {
  const { t: tShared } = useTranslation('project', {
    keyPrefix: 'Shared',
  });
  const { t } = useTranslation('project', {
    keyPrefix: 'Comments',
  });
  const { data: profileId } = useProfileId();

  useEffect(() => {
    if (
      !comment.seen &&
      comment.author_profile_id !== profileId &&
      !disableSeen
    ) {
      onUpdateSeen(comment.id);
    }
  }, [comment.id, comment.seen, comment.author_profile_id, profileId]);

  const handleDelete = () => {
    onDelete?.(comment.id);
  };

  const isOwnComment = comment.author_profile_id === profileId;

  return (
    <Card
      variant={'default'}
      className="rounded-sm border border-white/15 bg-transparent p-4"
    >
      <CardHeader className="rounded-none p-0">
        <CardTitle className="rounded-none p-0">
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center gap-4">
              <Avatar className="size-[39px] items-center justify-center border border-white/30">
                {comment.author?.avatar_url && (
                  <AvatarImage
                    src={comment.author.avatar_url}
                    alt={comment.author?.name ?? ''}
                  />
                )}
                <AvatarFallback>
                  {getInitials(comment.author?.name ?? 'P')}
                </AvatarFallback>
              </Avatar>
              <span className="text-2xs">
                {isOwnComment ? tShared('you') : comment.author?.name}
              </span>
            </div>
            <div>
              {isOwnComment && (
                <RoundedDropdownMenu
                  items={[
                    <DropdownMenuItem
                      key="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                    >
                      {t('delete')}
                    </DropdownMenuItem>,
                  ]}
                />
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="my-6 flex flex-col p-0 text-2xs">
        <div className="break-words">{comment.message}</div>
      </CardContent>
      <CardFooter className="flex flex-row justify-end p-0 text-4xs text-custom-medium-grey">
        <CardDescription>
          <div className="flex flex-row justify-end gap-2">
            {!comment.seen && !disableSeen && <div>{t('unseen')}</div>}
            {!comment.seen && !disableSeen && <div>·</div>}
            <div>
              {formatDistance(new Date(), comment.created_at, {
                locale: de,
              })}
            </div>
          </div>
        </CardDescription>
      </CardFooter>
    </Card>
  );
};
